import { ApiService } from '~/services/api.service';
import { addClientToUrl } from '~/utils/url.utils';
import { isViewerMode } from '~/utils/viewer-mode-flow.utils';
import { AxiosRequestConfig, AxiosError } from 'axios';
import { trackError } from '~/services/logger/error.utils';
import { GlobalCXContextValue } from '~/components/ClientExperience/providers/global-context/global-cx.context';
import { isHbDevServer, isReactFirst } from '~/utils/environment.utils';
import { HB_BASE_ERROR_CODES } from '~/services/interfaces/IApiService';
import { REACT_STORAGE_AUTH_KEY } from '~/common/storage.constants';

const ALLOWED_VIEWER_MODE_ROUTES = [
    /\/flow\/\w+\/view/,
    /\/flow\/\w+\/submit_view_only/
];

export class CXApiService extends ApiService {
    globalContext: GlobalCXContextValue;
    headers = {};

    constructor(globalContext: GlobalCXContextValue) {
        super(globalContext);
        this.globalContext = globalContext;
    }

    addHeader(header: Record<string, string>) {
        this.headers = { ...this.headers, ...header };
    }

    protected getBaseUrl(): string {
        const { configStore } = this.globalContext;

        if (configStore.config.environment === 'development') {
            return isReactFirst() && !isHbDevServer()
                ? 'http://localhost:8000/'
                : '/';
        }

        return super.getBaseUrl();
    }

    protected addClientToUrl(url: string): string {
        return addClientToUrl(url);
    }

    protected requestInterceptor({ url, params, headers, ...config }) {
        const intercepted = super.requestInterceptor({
            url,
            params,
            headers,
            ...config
        });

        // All CX API calls should contain /client/ in the URL (after api/v2/)
        const isOnMXApp = location.pathname.startsWith('/app/');
        if (!isOnMXApp) {
            intercepted.url = this.addClientToUrl(intercepted.url);
        }

        const {
            clientPortalConfigStore,
            configStore: {
                config: {
                    hb_api_headers: { view_only_hash }
                }
            },
            sessionStore: { limitedAuthStorageKey }
        } = this.globalContext;

        const ctxc = clientPortalConfigStore?.clientPortalCompanyId;
        if (ctxc && !intercepted.params.ctcx) {
            intercepted.params.ctxc = ctxc;
        }

        intercepted.headers = { ...intercepted.headers, ...this.headers };

        // View only auth
        if (isViewerMode()) {
            const hashObj = JSON.parse(
                localStorage.getItem(limitedAuthStorageKey)
            );
            intercepted.headers[view_only_hash] = hashObj?.hash;

            return this.viewerModeRequestInterceptor(intercepted);
        }

        return intercepted;
    }

    protected viewerModeRequestInterceptor(
        config: AxiosRequestConfig
    ): AxiosRequestConfig {
        const { url = '' } = config;
        if (
            ALLOWED_VIEWER_MODE_ROUTES.some(
                allowedViewerModeRoute => !!url.match(allowedViewerModeRoute)
            )
        ) {
            return config;
        }

        trackError({
            error: new Error('Unexpected http call in CXViewerMode'),
            context: {
                url,
                method: config.method
            }
        });

        return null;
    }

    protected onUnauthorizedError() {
        const { fullstoryService } = this.globalContext;
        fullstoryService.info('redirect to logout due to token expired');
        localStorage.removeItem(REACT_STORAGE_AUTH_KEY);
    }

    protected handleRoutineError(err: AxiosError<any>): Promise<any> {
        if (!err?.response?.data) {
            return Promise.reject(err);
        }

        const { error_type } = err.response.data;

        if (!error_type) {
            return Promise.reject(err.response);
        }

        const {
            configStore: {
                config: { hb_base_error_codes }
            }
        } = this.globalContext;

        // Log when falling back to default error codes for debugging
        if (!hb_base_error_codes) {
            trackError({
                error: new Error(
                    'hb_base_error_codes is undefined, falling back to HB_BASE_ERROR_CODES'
                ),
                context: {
                    error_type,
                    url: err.config?.url
                }
            });
        }

        const errorCodes = hb_base_error_codes || HB_BASE_ERROR_CODES;

        if (error_type === errorCodes.unauthorized_error) {
            this.onUnauthorizedError();
            return Promise.reject(err.response);
        }

        if (error_type === errorCodes.wrong_api_version_error) {
            trackError({
                error: new Error(`${errorCodes.wrong_api_version_error}`),
                message: 'Wrong server api version, reloading...'
            });

            window.location.reload();
        }

        return Promise.reject(err.response);
    }
}
